// /web/src/LandingPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { trackAmplitudeEvent } from "./lib/amplitude";
import PricingTierCards from "./components/PricingTierCards.jsx";
import { pricingTiers } from "./data/pricingTiers.js";
import { useBilling } from "./lib/api.js";

const HERO_IMAGES = [
  { src: "/assets/01_Circ.png", alt: "Circulation dashboard preview" },
  { src: "/assets/02_Lifts.png", alt: "Lift calculator preview" },
  { src: "/assets/03_WCs.png", alt: "WC provision preview" },
  { src: "/assets/04_Risers.png", alt: "Risers overview preview" },
];

const trackInteraction = (eventName, eventProperties = {}) => {
  if (typeof window === "undefined") return;
  try {
    trackAmplitudeEvent(eventName, eventProperties);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[Amplitude] track failed", { eventName, eventProperties, error });
    }
  }
};

function Hero() {
  const { loginWithRedirect } = useAuth0();
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 750);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const activeSlide = HERO_IMAGES[slideIndex];

  return (
    <section className="pd-section pd-hero" aria-labelledby="hero-title">
      <div className="pd-container pd-hero__grid">
        <div className="pd-hero__text">
          <h1 id="hero-title" className="pd-display">
            Design Automation Tools for Architects
          </h1>
          <p className="pd-lead">
            Automate the early design process. Pebbledash translates your brief into clear building metrics, from lift numbers to
            riser sizes, so every decision starts grounded in data.
          </p>

          <div className="pd-hero__actions">
            <button
              type="button"
              className="pd-btn pd-btn--primary"
              onClick={() => {
                trackInteraction("landing_free_trial_clicked");
                loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
              }}
            >
              Free trial
            </button>
          </div>
          <p className="pd-caption">Start with 5 free tokens to explore the tools.</p>
        </div>

        <div className="pd-hero__media">
          <div className="pd-hero__slideshow">
            <img src={activeSlide.src} alt={activeSlide.alt} />
          </div>
        </div>
      </div>
    </section>
  );
}

/*
function TrustedBy() {
  return (
    <section className="pd-section" aria-labelledby="trusted-title">
      <div className="pd-container">
        <h2 id="trusted-title" className="pd-eyebrow">
          Trusted by
        </h2>
        <ul className="pd-logo-grid" aria-label="Customer logos (placeholders)">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="pd-logo-card" aria-hidden="true" />
          ))}
        </ul>
      </div>
    </section>
  );
}
*/

function HowItWorks() {
  const videoRefs = useRef([]);
  const steps = [
    {
      n: "01",
      title: "Create a project",
      text: "Start a new project, input the location, use, area and floor heights. Pebbledash structures your brief for analysis.",
      media: {
        src: "/assets/01_project_setup.mp4",
        type: "video/mp4",
        alt: "Screen recording of creating a project in Pebbledash",
      },
    },
    {
      n: "02",
      title: "Run the calculators",
      text: "Use best-practice defaults or tune assumptions to generate lift counts, WC ratios, risers, end-of-trip facilities and more, all in seconds using a token-based system.",
      media: {
        src: "/assets/02_run_calculator.mp4",
        type: "video/mp4",
        alt: "Screen recording of running a Pebbledash calculator",
      },
    },
    {
      n: "03",
      title: "Export results",
      text: "Coming soon.",
    },
  ];

  useEffect(() => {
    if (typeof window === "undefined") return;

    const videos = videoRefs.current.filter(Boolean);
    if (videos.length === 0) return;

    const playVideo = (video) => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    };

    if (!("IntersectionObserver" in window)) {
      videos.forEach((video) => playVideo(video));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            playVideo(video);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videos.forEach((video) => observer.observe(video));

    return () => {
      videos.forEach((video) => observer.unobserve(video));
      observer.disconnect();
    };
  }, []);

  return (
    <section className="pd-section pd-section--tint-navy" aria-labelledby="how-title" id="howto">
      <div className="pd-container">
        <h2 id="how-title" className="pd-h2">
          How it works
        </h2>
        <p className="pd-lead">
          A clear process from input to insight. Every tool follows the same logic, keeping your workflow structured and fast.
        </p>

        <ol className="pd-steps--media">
          {steps.map((s, index) => (
            <li key={s.n} className="pd-step--media">
              <div className="pd-step__text">
                <span className="pd-step__num">{s.n}</span>
                <div>
                  <h3 className="pd-h3">{s.title}</h3>
                  <p className="pd-muted">{s.text}</p>
                </div>
              </div>
              <div className="pd-step__media">
                {s.media ? (
                  <video
                    className="pd-step__video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                    preload="metadata"
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    aria-label={s.media.alt}
                  >
                    <source src={s.media.src} type={s.media.type} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="pd-media-placeholder" role="img" aria-label={`Placeholder for ${s.title}`}>
                    Coming soon.
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    {
      title: "Lift traffic",
      text: "Predict waiting times, car loading, and intervals for every core scenario. Compare duty options before you commit.",
    },
    {
      title: "WC provision",
      text: "Check compliance with Part T, Part M, BCO and local guidance without spreadsheets. Surface pinch points early.",
    },
    {
      title: "Stairs",
      text: "Size escape stairs and convenience cores with immediate feedback on widths, runs, and occupancy limits.",
    },
    {
      title: "Cycling & amenities",
      text: "Fast counts for long-stay parking, showers and lockers across office, hospitality and residential mixes.",
    },
    {
      title: "Risers & plant (beta)",
      text: "Coordinate shafts and plant allowances with MEP before detailed design, including clustering and spares.",
    },
    {
      title: "Envelope & energy (beta)",
      text: "Iterate façade build-ups with U-value tools. Swap materials and keep energy targets in sight from the start.",
    },
  ];

  return (
    <section className="pd-section" aria-labelledby="features-title" id="benefits">
      <div className="pd-container">
        <div className="pd-features__head">
          <h2 id="features-title" className="pd-h2">
            Features
          </h2>
        </div>

        <div className="pd-features pd-features-grid">
          {items.map((f) => (
            <article key={f.title} className="pd-feature">
              <h3 className="pd-h3">{f.title}</h3>
              <p className="pd-muted">{f.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const billing = useBilling();

  return (
    <section className="pd-section pd-section--alt2" aria-labelledby="pricing-title" id="pricing">
      <div className="pd-container">
        <h2 id="pricing-title" className="pd-h2">
          Pricing
        </h2>
        <p className="pd-lead">Get started for free. Usage-based pricing as you grow.</p>

        <PricingTierCards
          tiers={pricingTiers}
          onTierSelect={async (tier) => {
            trackInteraction("landing_pricing_plan_clicked", {
              plan: tier.plan,
              label: tier.name,
            });

            if (!tier?.plan) return;

            if (!isAuthenticated) {
              try {
                await loginWithRedirect({
                  authorizationParams: { screen_hint: "signup" },
                  appState: { returnTo: `/pricing?plan=${tier.plan}` },
                });
              } catch (error) {
                console.error("Login required before starting checkout", error);
                alert("Please sign in to continue to checkout.");
              }
              return;
            }

            try {
              await billing.startPlanCheckout(tier.plan);
            } catch (error) {
              console.error("Unable to start checkout", error);
              alert(error?.message || "Could not start checkout. Please try again.");
            }
          }}
        />

        <p className="pd-caption">All prices exclude VAT.</p>
      </div>
    </section>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="pd-faq__item">
      <button
        type="button"
        className="pd-faq__button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="pd-h4">{q}</span>
        <span className="pd-faq__icon" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? <div className="pd-faq__panel pd-muted">{a}</div> : null}
    </li>
  );
}

function FAQ() {
  const qas = [
    {
      q: "What is Pebbledash?",
      a: (
        <p>
          Pebbledash is a web-based toolkit for architects, engineers, and developers that automates
          early-stage building design calculations, from lift traffic analysis and WC provision to
          stair sizing, riser planning, and more. It helps teams move from massing to metrics in minutes.
        </p>
      ),
    },
    {
      q: "Who is Pebbledash for?",
      a: (
        <p>
          It’s designed for architects, engineers, and property professionals who need quick, reliable
          building metrics during feasibility and concept design stages, particularly for office
          buildings in the UK.
        </p>
      ),
    },
    {
      q: "What calculators are available right now?",
      a: (
        <>
          <p>The live tools currently include:</p>
          <ul>
            <li>Lift Traffic Analysis</li>
            <li>WC Provision</li>
            <li>Staircase Provision</li>
            <li>Riser Calculator</li>
            <li>U-value Calculator</li>
            <li>Cycle Spaces</li>
          </ul>
          <p>New calculators like Plant, End-of-Trip Facilities, and Planning Context are in development.</p>
        </>
      ),
    },
    {
      q: "How accurate are the calculations?",
      a: (
        <p>
          Each calculator follows the relevant UK standards (e.g. CIBSE, BS6465, BCO guidance). The
          results are suitable for early design stages, providing benchmark values and compliance checks
          rather than detailed design outputs.
        </p>
      ),
    },
    {
      q: "How do I start a project?",
      a: (
        <p>
          Once logged in, go to Dashboard → New Project, enter your site details (postcode, use type,
          floors, occupancy), and input the areas for each floor. From there, you can launch any
          calculator using your project data.
        </p>
      ),
    },
    {
      q: "Do I need to install anything?",
      a: (
        <p>
          No. Pebbledash runs entirely in your browser. You can log in, create projects, and run
          calculations directly online, no software installation or CAD plugins required.
        </p>
      ),
    },
    {
      q: "How is pricing structured?",
      a: (
        <p>
          Pebbledash runs on a token-based system. Each calculation type has a fixed token cost. Tokens
          are refilled monthly based on your subscription plan (Lite, Studio, or Practice). See the
          Pricing page for details.
        </p>
      ),
    },
    {
      q: "Can I use Pebbledash for non-office buildings?",
      a: (
        <p>
          At present, all calculators are calibrated for UK office use. Residential, hotel, and mixed-use
          support will follow in later releases.
        </p>
      ),
    },
    {
      q: "Is my project data secure?",
      a: (
        <p>
          Yes. Pebbledash uses Auth0 for authentication and encrypted cloud storage. All calculations and
          project data are stored securely and never shared with third parties.
        </p>
      ),
    },
    {
      q: "What’s next for Pebbledash?",
      a: (
        <p>
          Upcoming releases will expand coverage across all UK Local Authorities for end-of-trip and
          cycle space requirements, introduce the plant calculator, and extend support to other use
          classes, before international expansion to Europe and the US.
        </p>
      ),
    },
  ];

  return (
    <section className="pd-section" aria-labelledby="faq-title">
      <div className="pd-container">
        <h2 id="faq-title" className="pd-h2">
          FAQs
        </h2>
        <ul className="pd-faq">
          {qas.map((qa) => (
            <FAQItem key={qa.q} {...qa} />
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function LandingPage() {
  useEffect(() => {
    trackInteraction("landing_page_viewed", {
      path: window.location.pathname,
      title: document.title,
    });
  }, []);

  return (
    <>
      <Hero />
      {/* <TrustedBy /> */}
      <HowItWorks />
      <Features />
      <PricingSection />
      <FAQ />
    </>
  );
}
